import React, { useState, useEffect, useRef, useSyncExternalStore } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Lightbox from '../components/Lightbox'

const MOCK = [
  { title: 'Sung Sot Cave',           image: null },
  { title: 'Ti Top Island',           image: null },
  { title: 'Vung Vieng Fishing Village', image: null },
  { title: 'Luon Cave',               image: null },
  { title: 'Pearl Farm',              image: null },
  { title: 'Dark & Bright Cave',      image: null },
  { title: 'Cap La Island',           image: null },
]

const MOCK_COLORS = ['#b8ccd8','#a8c0cc','#c0d0dc','#bcccd8','#c8d8e4','#b0c4d0','#c4d4e0']

function useVisible() {
  const w = useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth,
    () => 1024,
  )
  if (w >= 1024) return 4
  if (w >= 640)  return 3
  return 2
}

export default function Experiences({ experiences }) {
  const list    = experiences?.length ? experiences : MOCK
  const visible = useVisible()
  const [offset, setOffset]       = useState(0)
  const [lightboxIdx, setLightboxIdx] = useState(null)
  const hovered = useRef(false)
  const max = Math.max(0, list.length - visible)

  useEffect(() => { setOffset(o => Math.min(o, max)) }, [max])

  useEffect(() => {
    if (list.length <= visible) return
    const id = setInterval(() => {
      if (hovered.current) return
      setOffset(o => (o >= max ? 0 : o + 1))
    }, 4500)
    return () => clearInterval(id)
  }, [max, list.length, visible])

  // Only items with real images can open lightbox
  const realImages  = list.filter(item => item.image).map(item => item.image)
  const realTitles  = list.filter(item => item.image).map(item => item.title)
  // Map card index → lightbox index (only real images)
  const toLightbox  = (cardIdx) => {
    const item = list[cardIdx]
    if (!item?.image) return null
    return realImages.indexOf(item.image)
  }

  const prev = () => setOffset(o => Math.max(0, o - 1))
  const next = () => setOffset(o => Math.min(max, o + 1))

  return (
    <>
      <section className="si-section">
        <div className="si-experiences__header">
          <div>
            <h2 className="ljn-section-title">Experiences</h2>
            <div className="ljn-divider" style={{ marginBottom: 0 }} />
          </div>
          {list.length > visible && (
            <div className="si-experiences__nav">
              <button className="si-experiences__btn" onClick={prev} disabled={offset === 0} aria-label="Previous">
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <button className="si-experiences__btn" onClick={next} disabled={offset >= max} aria-label="Next">
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div
          className="si-experiences__track-wrap"
          onMouseEnter={() => { hovered.current = true }}
          onMouseLeave={() => { hovered.current = false }}
        >
          <div
            className="si-experiences__track"
            style={{ transform: `translateX(calc(-${offset} * ((100% + 10px) / ${visible})))` }}
          >
            {list.map((item, i) => {
              const lbIdx = toLightbox(i)
              return (
                <div
                  key={i}
                  className={`si-experience-card ${lbIdx !== null ? 'si-experience-card--clickable' : ''}`}
                  onClick={() => lbIdx !== null && setLightboxIdx(lbIdx)}
                >
                  <div
                    className="si-experience-card__img"
                    style={{ background: item.image ? undefined : MOCK_COLORS[i % MOCK_COLORS.length] }}
                  >
                    {item.image && <img src={item.image} alt={item.title} />}
                  </div>
                  <div className="si-experience-card__title">{item.title}</div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {lightboxIdx !== null && realImages.length > 0 && (
        <Lightbox
          images={realImages}
          startIdx={lightboxIdx}
          captions={realTitles}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  )
}
